import { useCallback, useState } from 'react';
import {
  ContentEngagement,
  EngagementSource,
  UserType,
} from '@amzn/awscat-pattern-library-service-model';

import { getUserTypeFromId } from '../../api/UtilsApi';
import { useContentEditorService } from '../../contexts/ContentEditorServiceContext';
import { useCurrentUser } from '../../contexts/UserContext';
import { useMplServiceClient } from '../../contexts/MplServiceClientContext';
import { log } from '../../loggers/Logger';
import { useLoadedDocument } from '../v2-hooks/useLoadedDocument';

/** Read-only presentation state for the "add customer influence" affordance. */
export interface CustomerInfluenceViewModel {
  /** Whether the affordance is shown at all (AWS-internal users only). */
  readonly isVisible: boolean;
  /** Whether the engagement modal is open. */
  readonly isModalOpen: boolean;
  /** The user's current engagements, loaded when the modal opens. */
  readonly engagements: readonly ContentEngagement[];
  /** True while engagements are loading or saving. */
  readonly isBusy: boolean;
  /** A load/save failure to show inline in the modal, or undefined. */
  readonly errorMessage: string | undefined;
  /** Whether saving is allowed — false until the existing engagements load succeeds. */
  readonly isSaveEnabled: boolean;
  /** Open the modal and load the user's existing engagements. */
  readonly openModal: () => void;
  /** Close the modal without saving. */
  readonly closeModal: () => void;
  /** Persist the given engagements (create or update), then close the modal. */
  readonly saveEngagements: (updated: ContentEngagement[]) => void;
}

/**
 * Drives the "add customer influence" button on the read-only view page. The
 * affordance is restricted to AWS-internal users. Opening it loads the user's
 * existing engagements; saving creates them (first time) or updates them.
 */
export function useCustomerInfluenceViewModel(): CustomerInfluenceViewModel | undefined {
  const { userId } = useCurrentUser();
  const mpl = useMplServiceClient();
  const document = useLoadedDocument();

  const contentId = document?.contentId;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [engagements, setEngagements] = useState<readonly ContentEngagement[]>([]);
  const [isBusy, setIsBusy] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  // Saving is gated on a successful load: a stale empty list would otherwise let
  // a save create engagements over ones that already exist on the server.
  const [isLoaded, setIsLoaded] = useState(false);

  const openModal = useCallback(async () => {
    if (!contentId) return;
    setIsModalOpen(true);
    setIsBusy(true);
    setErrorMessage(undefined);
    setIsLoaded(false);
    // Clear any list from a prior open so a failed load can't show stale rows.
    setEngagements([]);
    try {
      const response = await mpl.getContentEngagements(contentId, userId);
      setEngagements(response.contentEngagements ?? []);
      setIsLoaded(true);
    } catch (err) {
      // Keep the modal open and show the error rather than closing silently;
      // isLoaded stays false so saving can't overwrite existing engagements.
      log.error({ err, contentId }, 'Failed to load content engagements');
      setErrorMessage(
        err instanceof Error ? err.message : 'An unexpected error occurred. Please report a bug.',
      );
    } finally {
      setIsBusy(false);
    }
  }, [contentId, userId, mpl]);

  const closeModal = useCallback(() => setIsModalOpen(false), []);

  const saveEngagements = useCallback(
    async (updated: ContentEngagement[]) => {
      if (!contentId || !isLoaded) return;
      setIsBusy(true);
      setErrorMessage(undefined);
      try {
        // First-time engagements are created; subsequent edits update.
        if (engagements.length > 0) {
          await mpl.updateContentEngagements(contentId, userId, updated, EngagementSource.Guidance);
        } else {
          await mpl.createContentEngagements(contentId, userId, updated, EngagementSource.Guidance);
        }
        setEngagements(updated);
        setIsModalOpen(false);
      } catch (err) {
        // Keep the modal open so the user can retry; show the failure inline.
        log.error({ err, contentId }, 'Failed to save content engagements');
        setErrorMessage(
          err instanceof Error ? err.message : 'An unexpected error occurred. Please report a bug.',
        );
      } finally {
        setIsBusy(false);
      }
    },
    [contentId, userId, engagements, mpl, isLoaded],
  );

  // A parent-teardown render can transiently clear the document before this
  // view unmounts; return undefined so the view renders null instead of throwing.
  if (!document) return undefined;

  const isVisible = getUserTypeFromId(userId) === UserType.AWS;

  return {
    isVisible,
    isModalOpen,
    engagements,
    isBusy,
    errorMessage,
    isSaveEnabled: isLoaded,
    openModal,
    closeModal,
    saveEngagements,
  };
}
